import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ServerIcon, CheckCircleIcon, XCircleIcon, ChartBarSquareIcon, PauseCircleIcon, PlayCircleIcon } from '@heroicons/react/24/outline';
import { useConnection } from '../../contexts/EnhancedConnectionContext';
import { useUI } from '../../contexts/UIContext';
import { getSchemaStats, groupTablesBySchema } from '../../utils/schemaHierarchy';
import { useTablesData } from '../../hooks/useTablesData';
import { useAvailableSchemas } from '../../hooks/useAvailableSchemas';
import { useAutomationStatus } from '../../hooks/useAutomationStatus';
import SnowflakeConnectionForm from './components/SnowflakeConnectionForm';
import PostgreSQLConnectionForm from './components/PostgreSQLConnectionForm';
import BigQueryConnectionForm from './components/BigQueryConnectionForm';
import SelectionSummaryCards from '../../components/connections/SelectionSummaryCards';
import SchemaSelectionComponent from '../../components/metadata/SchemaSelectionComponent';

// Helper function to format bytes
const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const ConnectionDetailPage = () => {
  const { connectionSlug } = useParams();
  const navigate = useNavigate();
  const { getConnection, getConnectionBySlug, updateConnection, testConnection, refreshConnectionsNow, loading: connectionLoading } = useConnection();
  const { updateBreadcrumbs, showNotification, setLoading } = useUI();

  // Resolve connection from slug synchronously at render time (not inside useEffect)
  // This prevents infinite loops from function dependencies changing
  const resolvedConnection = connectionSlug ? getConnectionBySlug(connectionSlug) : null;
  const resolvedConnectionId = resolvedConnection?.id;

  const [connection, setConnection] = useState(null);
  const [connectionName, setConnectionName] = useState('');
  const [connectionDetails, setConnectionDetails] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedDatabases, setSelectedDatabases] = useState([]);
  const [selectedSchemas, setSelectedSchemas] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [activeTab, setActiveTab] = useState('connection');
  const [showModifySelection, setShowModifySelection] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [monitoringToggling, setMonitoringToggling] = useState(false);

  // Master per-connection pause switch. Backed by the existing
  // `toggle_connection_automation` API which, as of 2026-05, gates BOTH the
  // tier scheduler (`database_connections.enabled`) and the legacy job
  // scheduler (`automation_connection_configs.schedule_config.*.enabled`).
  // Reads current state from `connection.enabled` because that column is
  // the tier scheduler's source of truth and stays in sync after toggles.
  const { toggleConnectionAutomation } = useAutomationStatus(connection?.id);
  const monitoringEnabled = connection?.enabled !== false;

  const handleToggleMonitoring = useCallback(async () => {
    if (!connection?.id || monitoringToggling) return;
    const newValue = !monitoringEnabled;
    setMonitoringToggling(true);
    try {
      const success = await toggleConnectionAutomation(newValue);
      if (success) {
        // Optimistic local update. We can't call getConnection() here —
        // it reads from the cached connections array, which won't reflect
        // the just-written value until refreshConnectionsNow() settles.
        // The backend already confirmed the write, so we set local state
        // directly. refreshConnectionsNow() then updates the shared cache
        // for the Connections-page badge.
        setConnection((prev) => (prev ? { ...prev, enabled: newValue } : prev));

        if (refreshConnectionsNow) {
          refreshConnectionsNow().catch((err) =>
            console.warn('Could not refresh connections list after toggle:', err)
          );
        }

        showNotification(
          newValue
            ? 'Monitoring resumed — all scheduled jobs will run on their normal intervals'
            : 'Monitoring paused — no scheduled jobs will run on this connection',
          'success'
        );
      } else {
        showNotification('Failed to update monitoring state', 'error');
      }
    } catch (err) {
      showNotification(`Error updating monitoring: ${err?.message || 'Unknown error'}`, 'error');
    } finally {
      setMonitoringToggling(false);
    }
  }, [connection, monitoringEnabled, monitoringToggling, toggleConnectionAutomation, refreshConnectionsNow, showNotification]);

  // Fetch tables data for schema grouping
  // Use showAllCached=true to get ALL cached tables, not just from selected schemas
  // This allows showing table counts for previously selected but now unselected schemas
  const { data: tablesData, isLoading: tablesLoading, error: tablesError } = useTablesData(connection?.id, {
    enabled: !!connection?.id && connection.id !== 'undefined',
    staleTime: 0, // Always fetch fresh data
    gcTime: 0,  // Don't cache (renamed from cacheTime in RQ v5)
    forceFresh: true, // Bypass API service cache
    showAllCached: true // Show ALL cached tables, not just from selected schemas
  });

  // Fetch ALL available schemas directly from database (including empty ones)
  const { data: availableSchemas, isLoading: schemasLoading } = useAvailableSchemas(connection?.id, {
    enabled: !!connection?.id && connection.id !== 'undefined'
  });

  // Debug logging
  React.useEffect(() => {
    console.log('ConnectionDetailPage - Debug Info:', {
      connectionId: connection?.id,
      tablesData,
      tablesLoading,
      tablesError,
      hasTablesArray: !!tablesData?.tables,
      tablesCount: tablesData?.tables?.length,
      firstThreeTables: tablesData?.tables?.slice(0, 3).map(t => ({
        name: t.name,
        database: t.database,
        schema: t.schema
      }))
    });
  }, [connection?.id, tablesData, tablesLoading, tablesError]);

  // Merge available schemas with cached table data
  // This allows selecting ANY schema in the database, even empty ones
  const groupedSchemas = React.useMemo(() => {
    console.log('[ConnectionDetailPage] Merging available schemas with cached table data');

    // Start with all available schemas from database
    const allSchemas = {};

    if (availableSchemas?.schemas) {
      availableSchemas.schemas.forEach(schema => {
        allSchemas[schema.full_name] = {
          name: schema.full_name,
          database: schema.database,
          schema: schema.schema,
          tables: [],
          table_count: 0,
          total_rows: 0,
          size_bytes: 0,
          _no_cache: true  // Flag to indicate no cached data yet
        };
      });
    }

    // Overlay cached table data
    if (tablesData?.tables) {
      tablesData.tables.forEach(table => {
        const database = table.database || 'UNKNOWN_DB';
        const schema = table.schema || 'UNKNOWN_SCHEMA';
        const fullSchemaName = `${database}.${schema}`;

        if (!allSchemas[fullSchemaName]) {
          // Schema not in available list (shouldn't happen), add it anyway
          allSchemas[fullSchemaName] = {
            name: fullSchemaName,
            database: database,
            schema: schema,
            tables: [],
            table_count: 0,
            total_rows: 0,
            size_bytes: 0
          };
        }

        // Add table data
        allSchemas[fullSchemaName].tables.push(table);
        allSchemas[fullSchemaName].table_count += 1;
        allSchemas[fullSchemaName].total_rows += table.row_count || 0;
        allSchemas[fullSchemaName].size_bytes += table.size_bytes || 0;
        delete allSchemas[fullSchemaName]._no_cache; // Has cached data
      });
    }

    const result = Object.values(allSchemas);
    console.log('[ConnectionDetailPage] Merged schemas:', {
      totalSchemas: result.length,
      schemasWithCache: result.filter(s => !s._no_cache).length,
      schemasWithoutCache: result.filter(s => s._no_cache).length,
      samples: result.slice(0, 5).map(s => ({
        name: s.name,
        tableCount: s.table_count,
        hasCache: !s._no_cache
      }))
    });

    return result;
  }, [availableSchemas, tablesData]);

  // Set breadcrumbs
  useEffect(() => {
    updateBreadcrumbs([
      { name: 'Connections', href: '/connections' },
      { name: 'Edit Connection' }
    ]);
  }, [updateBreadcrumbs]);

  // Load connection - depends only on resolvedConnectionId (primitive) to prevent infinite loops
  useEffect(() => {
    const loadConnection = async () => {
      // Skip if no resolved connection ID yet (still loading or not found)
      if (!resolvedConnectionId) {
        return;
      }

      // Skip if we already have this connection loaded
      if (connection?.id === resolvedConnectionId) {
        return;
      }

      try {
        setLoading('connection', true);

        // Fetch full connection details using the resolved UUID
        const connectionData = await getConnection(resolvedConnectionId);
        setConnection(connectionData);
        setConnectionName(connectionData.name);
        setConnectionDetails(connectionData.connection_details || {});
        setSelectedDatabases(connectionData.selected_databases || []);
        setSelectedSchemas(connectionData.selected_schemas || []);
        setSelectedTables(connectionData.selected_tables || []);
      } catch (error) {
        console.error('Error loading connection:', error);
        showNotification('Failed to load connection', 'error');
        navigate('/connections');
      } finally {
        setLoading('connection', false);
      }
    };

    loadConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedConnectionId]); // Only depend on primitive value to prevent infinite loops

  // Handle "not found" case separately - only after connections have loaded
  useEffect(() => {
    if (!connectionLoading && connectionSlug && !resolvedConnection) {
      console.error('Connection not found for slug:', connectionSlug);
      showNotification('Connection not found', 'error');
      navigate('/connections');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionLoading, connectionSlug, resolvedConnection]);

  // Handle connection name change
  const handleNameChange = (e) => {
    setConnectionName(e.target.value);
  };

  // Handle connection details change
  const handleDetailsChange = (details) => {
    setConnectionDetails(details);
    setTestResult(null);
  };

  // Test connection with better validation
  const handleTestConnection = async () => {
    // Clear previous test result
    setTestResult(null);

    // Validate form
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      showNotification('Please fix the form errors before testing', 'warning');
      return;
    }

    try {
      setTestLoading(true);

      const testData = {
        connection_type: connection.connection_type, // or connection.connection_type for edit page
        connection_details: connectionDetails
      };

      console.log('Testing connection with:', JSON.stringify(testData, null, 2));
      const result = await testConnection(testData);

      // Check for specific success indicators in the result
      const isSuccess = result.success === true ||
                        result.message?.toLowerCase().includes('success') ||
                        result.status === 'success';

      if (isSuccess) {
        setTestResult({
          success: true,
          message: 'Connection successful!',
          details: result
        });

        showNotification('Connection test successful!', 'success');
      } else {
        // If we got a response but no clear success indicator, treat as failure
        setTestResult({
          success: false,
          message: 'Connection failed - unexpected response',
          details: result
        });

        showNotification('Connection test failed with an unexpected response', 'error');
      }
    } catch (error) {
      console.error('Connection test failed:', error);

      setTestResult({
        success: false,
        message: 'Connection failed',
        details: error.response?.data?.error || error.message
      });

      showNotification(`Connection test failed: ${error.response?.data?.error || error.message}`, 'error');
    } finally {
      setTestLoading(false);
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!connectionName.trim()) {
      errors.name = 'Connection name is required';
    }

    // Validate details based on connection type
    if (connection?.connection_type === 'snowflake') {
      if (!connectionDetails.account) errors.account = 'Account is required';
      if (!connectionDetails.username) errors.username = 'Username is required';
      if (!connectionDetails.password && !connection.connection_details.password) {
        errors.password = 'Password is required';
      }
      if (!connectionDetails.database) errors.database = 'Database is required';
      if (!connectionDetails.warehouse) errors.warehouse = 'Warehouse is required';
    } else if (connection?.connection_type === 'postgresql') {
      if (!connectionDetails.host) errors.host = 'Host is required';
      if (!connectionDetails.port) errors.port = 'Port is required';
      if (!connectionDetails.database) errors.database = 'Database is required';
      if (!connectionDetails.username) errors.username = 'Username is required';
      if (!connectionDetails.password && !connection.connection_details.password) {
        errors.password = 'Password is required';
      }
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    // Validate form
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    try {
      setIsSubmitting(true);

      // Handle empty password field - don't update it if not changed
      const updatedDetails = { ...connectionDetails };
      if (!updatedDetails.password && connection.connection_details.password) {
        // Use placeholder to indicate we're not changing the password
        updatedDetails.password = undefined;
      }

      const connectionData = {
        name: connectionName,
        connection_type: connection.connection_type,
        connection_details: updatedDetails,
        selected_databases: selectedDatabases,
        selected_schemas: selectedSchemas,
        selected_tables: selectedTables
      };

      await updateConnection(connection.id, connectionData);

      showNotification('Connection updated successfully!', 'success');

      // Exit edit mode after successful save
      setIsEditMode(false);

      // If we're on the schema selection tab, don't navigate away
      if (activeTab === 'tables') {
        showNotification('Schema selection saved successfully!', 'success');
      } else {
        // For connection settings, stay on page but exit edit mode
        // navigate('/connections');
      }
    } catch (error) {
      console.error('Failed to update connection:', error);
      showNotification(`Failed to update connection: ${error.response?.data?.error || error.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle save specifically for schema selection
  const handleSaveTableSelection = async () => {
    try {
      setIsSubmitting(true);

      // Schema-first architecture: Just send selected schemas
      // Backend will auto-populate selected_tables as a cache
      const connectionData = {
        selected_databases: selectedDatabases,
        selected_schemas: selectedSchemas
        // Don't send selected_tables - backend computes it from schemas
      };

      console.log('Saving schema selection:', {
        databases: selectedDatabases,
        schemas: selectedSchemas
      });

      await updateConnection(connection.id, connectionData);
      showNotification(`Schema selection saved! ${selectedSchemas.length} schema${selectedSchemas.length !== 1 ? 's' : ''} selected.`, 'success');

      // Close the modify selection interface
      setShowModifySelection(false);

      // Reload connection data to confirm save
      const updatedConnection = await getConnection(connection.id);
      setConnection(updatedConnection);

    } catch (error) {
      console.error('Failed to save schema selection:', error);
      showNotification(`Failed to save schema selection: ${error.response?.data?.error || error.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // If connection not loaded yet, show loading state
  if (connectionLoading || !connection) {
    return (
      <div className="py-4">
        <div className="animate-pulse">
          <div className="h-6 bg-secondary-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-secondary-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-secondary-900">Edit Connection</h1>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={handleToggleMonitoring}
            disabled={monitoringToggling || !connection?.id}
            className={`inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 ${
              monitoringEnabled
                ? 'border-secondary-300 bg-white text-secondary-700 hover:bg-secondary-50'
                : 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
            }`}
            title={monitoringEnabled
              ? 'Pause all scheduled automation for this connection'
              : 'Resume scheduled automation for this connection'}
          >
            {monitoringEnabled ? (
              <>
                <PauseCircleIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                Pause monitoring
              </>
            ) : (
              <>
                <PlayCircleIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                Resume monitoring
              </>
            )}
          </button>
          <Link
            to="/connections"
            className="inline-flex items-center px-4 py-2 border border-secondary-300 rounded-md shadow-sm text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <ArrowLeftIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Back to Connections
          </Link>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mt-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('connection')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'connection'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Connection Settings
            </button>
            <button
              onClick={() => setActiveTab('tables')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'tables'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Schema Selection
            </button>
            <button
              onClick={() => setActiveTab('anomalies')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'anomalies'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Anomaly Detection
            </button>
          </nav>
        </div>
      </div>

      {/* Connection Settings Tab */}
      {activeTab === 'connection' && (
        <div className="mt-6 bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="md:grid md:grid-cols-3 md:gap-6">
              <div className="md:col-span-1">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-secondary-900">Connection Details</h3>
                    <p className="mt-1 text-sm text-secondary-500">
                      {isEditMode 
                        ? `Edit connection settings for ${connection.name}.`
                        : `View connection settings for ${connection.name}.`
                      }
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setIsEditMode(!isEditMode);
                      if (isEditMode) {
                        // If exiting edit mode, reset errors
                        setErrors({});
                        setTestResult(null);
                      }
                    }}
                    className={`inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      isEditMode 
                        ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-indigo-500' 
                        : 'border-transparent text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                    }`}
                  >
                    {isEditMode ? (
                      <>
                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Cancel Edit
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </>
                    )}
                  </button>
                </div>

            {testResult && (
              <div className={`mt-4 p-4 rounded-md ${testResult.success ? 'bg-accent-50' : 'bg-danger-50'}`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {testResult.success ? (
                      <CheckCircleIcon className="h-5 w-5 text-accent-400" aria-hidden="true" />
                    ) : (
                      <XCircleIcon className="h-5 w-5 text-danger-400" aria-hidden="true" />
                    )}
                  </div>
                  <div className="ml-3">
                    <h3 className={`text-sm font-medium ${testResult.success ? 'text-accent-800' : 'text-danger-800'}`}>
                      {testResult.message}
                    </h3>
                    {testResult.details && (
                      <div className="mt-2 text-sm">
                        {typeof testResult.details === 'object' ? (
                          <pre className="text-xs overflow-auto p-2 bg-white rounded">
                            {JSON.stringify(testResult.details, null, 2)}
                          </pre>
                        ) : (
                          <p>{testResult.details}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
              </div>

              <div className="mt-5 md:mt-0 md:col-span-2">
                {!isEditMode ? (
                  // Read-only summary view
                  <div className="bg-gray-50 rounded-lg p-6 space-y-6">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Connection Summary</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Name</dt>
                          <dd className="mt-1 text-sm text-gray-900">{connectionName}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Type</dt>
                          <dd className="mt-1 text-sm text-gray-900 capitalize">{connection.connection_type}</dd>
                        </div>
                      </div>
                    </div>
                    
                    {connection.connection_type === 'snowflake' && (
                      <div>
                        <h5 className="text-sm font-medium text-gray-900 mb-3">Snowflake Details</h5>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Account</dt>
                            <dd className="mt-1 text-sm text-gray-900">{connectionDetails.account || 'Not configured'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Username</dt>
                            <dd className="mt-1 text-sm text-gray-900">{connectionDetails.username || 'Not configured'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Database</dt>
                            <dd className="mt-1 text-sm text-gray-900">{connectionDetails.database || 'Not configured'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Warehouse</dt>
                            <dd className="mt-1 text-sm text-gray-900">{connectionDetails.warehouse || 'Not configured'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Schema</dt>
                            <dd className="mt-1 text-sm text-gray-900">{connectionDetails.schema || 'PUBLIC (default)'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Role</dt>
                            <dd className="mt-1 text-sm text-gray-900">{connectionDetails.role || 'Not specified'}</dd>
                          </div>
                        </div>
                      </div>
                    )}

                    {connection.connection_type === 'postgresql' && (
                      <div>
                        <h5 className="text-sm font-medium text-gray-900 mb-3">PostgreSQL Details</h5>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Host</dt>
                            <dd className="mt-1 text-sm text-gray-900">{connectionDetails.host || 'Not configured'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Port</dt>
                            <dd className="mt-1 text-sm text-gray-900">{connectionDetails.port || '5432'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Database</dt>
                            <dd className="mt-1 text-sm text-gray-900">{connectionDetails.database || 'Not configured'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Username</dt>
                            <dd className="mt-1 text-sm text-gray-900">{connectionDetails.username || 'Not configured'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Schema</dt>
                            <dd className="mt-1 text-sm text-gray-900">{connectionDetails.schema || 'public (default)'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">SSL</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                connectionDetails.useSSL !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {connectionDetails.useSSL !== false ? 'Enabled' : 'Disabled'}
                              </span>
                            </dd>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="pt-4 border-t border-gray-200">
                      <div className="text-xs text-gray-500">
                        Password and other sensitive details are hidden for security.
                      </div>
                    </div>
                  </div>
                ) : (
                  // Editable form view
                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-6 gap-6">
                      <div className="col-span-6 sm:col-span-3">
                        <label htmlFor="connection-name" className="block text-sm font-medium text-secondary-700">
                          Connection Name
                        </label>
                        <input
                          type="text"
                          name="connection-name"
                          id="connection-name"
                          value={connectionName}
                          onChange={handleNameChange}
                          className={`mt-1 block w-full shadow-sm sm:text-sm border-secondary-300 rounded-md focus:ring-primary-500 focus:border-primary-500 ${errors.name ? 'border-danger-300' : ''}`}
                        />
                        {errors.name && (
                          <p className="mt-2 text-sm text-danger-600">{errors.name}</p>
                        )}
                      </div>

                      <div className="col-span-6 sm:col-span-3">
                        <label htmlFor="connection-type" className="block text-sm font-medium text-secondary-700">
                          Connection Type
                        </label>
                        <input
                          type="text"
                          name="connection-type"
                          id="connection-type"
                          value={connection.connection_type}
                          disabled
                          className="mt-1 block w-full py-2 px-3 border border-secondary-300 bg-secondary-100 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        />
                      </div>

                      <div className="col-span-6">
                        {connection.connection_type === 'snowflake' && (
                          <SnowflakeConnectionForm
                            details={connectionDetails}
                            onChange={handleDetailsChange}
                            errors={errors}
                            readOnly={false}
                          />
                        )}

                        {connection.connection_type === 'postgresql' && (
                          <PostgreSQLConnectionForm
                            details={connectionDetails}
                            onChange={handleDetailsChange}
                            errors={errors}
                            readOnly={false}
                          />
                        )}

                        {connection.connection_type === 'bigquery' && (
                          <BigQueryConnectionForm
                            details={connectionDetails}
                            onChange={handleDetailsChange}
                            errors={errors}
                            readOnly={false}
                          />
                        )}
                      </div>
                    </div>

                    <div className="mt-8 flex justify-end space-x-4">
                      <button
                        type="button"
                        onClick={handleTestConnection}
                        disabled={testLoading}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        {testLoading ? (
                          <>
                            <ServerIcon className="animate-pulse -ml-1 mr-2 h-5 w-5" aria-hidden="true"/>
                            Testing...
                          </>
                        ) : (
                          <>
                            <ServerIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true"/>
                            Test Connection
                          </>
                        )}
                      </button>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {isSubmitting ? 'Updating...' : 'Save Changes'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schema Selection Tab */}
      {activeTab === 'tables' && (
        <div className="mt-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
            <div className="md:grid md:grid-cols-3 md:gap-6">
              <div className="md:col-span-1">
                <h3 className="text-lg font-medium leading-6 text-secondary-900">Schema Selection</h3>
                <p className="mt-1 text-sm text-secondary-500">
                  Select schemas to monitor. All tables in selected schemas will be automatically included, including new tables added later.
                </p>
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-xs text-blue-700">
                    <strong>Automatic Detection:</strong> Schema-first selection enables automatic detection of new tables and schema changes without manual updates.
                  </p>
                </div>
              </div>

              <div className="mt-5 md:mt-0 md:col-span-2">

                {/* Selection Summary Cards */}
                <SelectionSummaryCards
                  selectedDatabases={selectedDatabases}
                  selectedSchemas={selectedSchemas}
                  selectedTables={selectedTables}
                  onModifySelection={() => setShowModifySelection(true)}
                />

                {/* Schema-Level Summary */}
                {selectedSchemas.length > 0 && (
                  <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Selected Schemas</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {selectedSchemas.map(schemaName => {
                        // Find the schema in groupedSchemas to get accurate table count
                        const schemaData = groupedSchemas.find(s => s.name === schemaName);
                        const tableCount = schemaData?.table_count || 0;
                        const rowCount = schemaData?.total_rows || 0;
                        const sizeBytes = schemaData?.size_bytes || 0;

                        return (
                          <div key={schemaName} className="bg-white border border-gray-200 rounded-md p-3">
                            <div className="mb-2">
                              <span className="text-sm font-medium text-gray-900">{schemaName}</span>
                            </div>
                            <div className="space-y-1 text-xs text-gray-600">
                              <div className="flex items-center justify-between">
                                <span>Tables:</span>
                                <span className="font-medium">{tableCount}</span>
                              </div>
                              {rowCount > 0 && (
                                <div className="flex items-center justify-between">
                                  <span>Rows:</span>
                                  <span className="font-medium">{rowCount.toLocaleString()}</span>
                                </div>
                              )}
                              {sizeBytes > 0 && (
                                <div className="flex items-center justify-between">
                                  <span>Size:</span>
                                  <span className="font-medium">{formatBytes(sizeBytes)}</span>
                                </div>
                              )}
                              {!schemaData && (
                                <div className="text-amber-600 italic">Not found in current tables</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Schema Selection Interface - Show when modifying */}
                {showModifySelection && (
                  <div className="mt-6 border-t pt-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-lg font-medium text-gray-900">Modify Schema Selection</h4>
                      <button
                        onClick={() => setShowModifySelection(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <span className="sr-only">Close</span>
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <SchemaSelectionComponent
                      connectionId={connection?.id}
                      selectedSchemas={selectedSchemas}
                      onSelectionChange={(schemas) => {
                        // Schema-first only: Just update selected schemas
                        // Backend will auto-populate selected_tables when saved
                        setSelectedSchemas(schemas);
                      }}
                      showMetadata={true}
                      multiSelect={true}
                      schemas={groupedSchemas}
                      loading={tablesLoading}
                    />
                  </div>
                )}

                {/* Save Button */}
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleSaveTableSelection}
                    disabled={isSubmitting}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Saving...' : `Save Selection${selectedSchemas.length > 0 ? ` (${selectedSchemas.length} schema${selectedSchemas.length !== 1 ? 's' : ''})` : ''}`}
                  </button>
                </div>
              </div>
            </div>
        </div>
      )}

      {/* Anomaly Detection Tab */}
      {activeTab === 'anomalies' && (
        <div className="mt-6 bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="md:grid md:grid-cols-3 md:gap-6">
              <div className="md:col-span-1">
                <h3 className="text-lg font-medium leading-6 text-secondary-900">Anomaly Detection</h3>
                <p className="mt-1 text-sm text-secondary-500">
                  Configure automated anomaly detection for your data. Monitor data quality metrics and get alerted when anomalies are detected.
                </p>
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-xs text-blue-700">
                    <strong>Note:</strong> Anomaly detection runs automatically based on your configured schedule.
                  </p>
                </div>
              </div>
              
              <div className="mt-5 md:mt-0 md:col-span-2">
                <div className="space-y-6">
                  {/* Quick Actions. After the 2026-05-19 monitors
                      unification, "View Dashboard" + "Manage Configurations"
                      both point to the same unified /monitors page (one
                      tab for ML/statistical, one for thresholds — the
                      old configs flow lives inside the new form).
                      Buttons only render when connection.slug is known —
                      during the brief load window connection is undefined,
                      and the template literal would emit /monitors/undefined,
                      a route that matches :connectionSlug and lands the
                      user on the "No connection selected" empty state. */}
                  {connection?.slug && (
                  <div className="flex space-x-4">
                    <Link
                      to={`/monitors/${connection.slug}`}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <ChartBarSquareIcon className="h-4 w-4 mr-2" />
                      View Monitors
                    </Link>
                    <Link
                      to={`/monitors/${connection.slug}/new`}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      New Monitor
                    </Link>
                  </div>
                  )}

                  {/* Status Summary */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Anomaly Detection Status</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Status:</span>
                        <span className="ml-2 font-medium text-green-600">Active</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Last Run:</span>
                        <span className="ml-2 font-medium">Today at 12:52 PM</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Configurations:</span>
                        <span className="ml-2 font-medium">3 active</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Next Run:</span>
                        <span className="ml-2 font-medium">Next Friday 12:36 PM</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ConnectionDetailPage;